import React, { useState } from "react";
import api from "../services/api";
import "./FormProyecto.css";

const CrearProyecto = () => {
  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    ubicacion: "",
    fecha_inicio: "",
    fecha_fin: "",
    presupuesto: "",
    responsable: "",
    prioridad: "media",
    notas: ""
  });

  const [errores, setErrores] = useState({});
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Limpiar error del campo al escribir
    if (errores[name]) {
      setErrores({ ...errores, [name]: "" });
    }
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    if (!form.nombre || form.nombre.trim().length < 3) {
      nuevosErrores.nombre = "El nombre debe tener al menos 3 caracteres";
    }

    if (!form.fecha_inicio) {
      nuevosErrores.fecha_inicio = "La fecha de inicio es obligatoria";
    }

    if (form.fecha_fin && form.fecha_inicio) {
      if (new Date(form.fecha_fin) < new Date(form.fecha_inicio)) {
        nuevosErrores.fecha_fin = "La fecha de fin debe ser posterior a la de inicio";
      }
    }

    if (form.presupuesto && parseFloat(form.presupuesto) < 0) {
      nuevosErrores.presupuesto = "El presupuesto no puede ser negativo";
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const guardar = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      setMensaje({
        tipo: "error",
        texto: "Por favor, corrige los errores en el formulario"
      });
      return;
    }

    setLoading(true);
    setMensaje(null);

    try {
      const datosProyecto = {
        ...form,
        presupuesto: form.presupuesto ? parseFloat(form.presupuesto) : 0
      };

      await api.post("/proyectos", datosProyecto);
      
      setMensaje({
        tipo: "exito",
        texto: "Proyecto creado exitosamente"
      });

      // Redirigir después de 1.5 segundos
      setTimeout(() => {
        window.location.href = "/proyectos";
      }, 1500);
    } catch (error) {
      console.error("Error guardando proyecto:", error);
      
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.detalles?.[0] ||
                      "Error al guardar el proyecto";
      
      setMensaje({
        tipo: "error",
        texto: errorMsg
      });
      setLoading(false);
    }
  };

  const cancelar = () => {
    if (window.confirm("¿Estás seguro de cancelar? Los cambios no guardados se perderán.")) {
      window.location.href = "/proyectos";
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <div>
          <h1>Crear Nuevo Proyecto</h1>
          <p className="subtitle">Completa la información del proyecto</p>
        </div>
        <button onClick={cancelar} className="btn-cerrar" title="Cerrar">
          ✕
        </button>
      </div>

      {mensaje && (
        <div className={`mensaje ${mensaje.tipo}`}>
          <span className="mensaje-icon">
            {mensaje.tipo === "exito" ? "✓" : "⚠"}
          </span>
          <span>{mensaje.texto}</span>
        </div>
      )}

      <form onSubmit={guardar} className="proyecto-form">
        {/* Información básica */}
        <div className="form-section">
          <h3 className="section-title">Información Básica</h3>
          
          <div className="form-row">
            <div className="form-group full">
              <label htmlFor="nombre">
                Nombre del Proyecto <span className="required">*</span>
              </label>
              <input
                id="nombre"
                name="nombre"
                type="text"
                placeholder="Ej: Construcción Edificio Central"
                value={form.nombre}
                onChange={handleChange}
                className={errores.nombre ? "error" : ""}
                disabled={loading}
              />
              {errores.nombre && <span className="error-text">{errores.nombre}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full">
              <label htmlFor="descripcion">Descripción</label>
              <textarea
                id="descripcion"
                name="descripcion"
                placeholder="Describe brevemente el proyecto..."
                value={form.descripcion}
                onChange={handleChange}
                rows="4"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ubicacion">Ubicación</label>
              <input
                id="ubicacion"
                name="ubicacion"
                type="text"
                placeholder="Ej: Santo Domingo, DN"
                value={form.ubicacion}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="responsable">Responsable</label>
              <input
                id="responsable"
                name="responsable"
                type="text"
                placeholder="Nombre del responsable"
                value={form.responsable}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Fechas y planificación */}
        <div className="form-section">
          <h3 className="section-title">Fechas y Planificación</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fecha_inicio">
                Fecha de Inicio <span className="required">*</span>
              </label>
              <input
                id="fecha_inicio"
                name="fecha_inicio"
                type="date"
                value={form.fecha_inicio}
                onChange={handleChange}
                className={errores.fecha_inicio ? "error" : ""}
                disabled={loading}
              />
              {errores.fecha_inicio && (
                <span className="error-text">{errores.fecha_inicio}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="fecha_fin">Fecha de Finalización</label>
              <input
                id="fecha_fin"
                name="fecha_fin"
                type="date"
                value={form.fecha_fin}
                onChange={handleChange}
                className={errores.fecha_fin ? "error" : ""}
                disabled={loading}
                min={form.fecha_inicio}
              />
              {errores.fecha_fin && (
                <span className="error-text">{errores.fecha_fin}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="prioridad">Prioridad</label>
              <select
                id="prioridad"
                name="prioridad"
                value={form.prioridad}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="baja">🟢 Baja</option>
                <option value="media">🟡 Media</option>
                <option value="alta">🟠 Alta</option>
                <option value="urgente">🔴 Urgente</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="presupuesto">Presupuesto (RD$)</label>
              <input
                id="presupuesto"
                name="presupuesto"
                type="number"
                placeholder="0.00"
                value={form.presupuesto}
                onChange={handleChange}
                className={errores.presupuesto ? "error" : ""}
                disabled={loading}
                min="0"
                step="0.01"
              />
              {errores.presupuesto && (
                <span className="error-text">{errores.presupuesto}</span>
              )}
            </div>
          </div>
        </div>

        {/* Notas adicionales */}
        <div className="form-section">
          <h3 className="section-title">Notas Adicionales</h3>
          
          <div className="form-row">
            <div className="form-group full">
              <label htmlFor="notas">Notas</label>
              <textarea
                id="notas"
                name="notas"
                placeholder="Información adicional, observaciones, etc."
                value={form.notas}
                onChange={handleChange}
                rows="4"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <button
            type="button"
            onClick={cancelar}
            className="btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Guardando...
              </>
            ) : (
              <>
                <span className="icon">💾</span>
                Crear Proyecto
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CrearProyecto;